// utils
import { paramCase } from 'src/utils/change-case';
import { _id, _postTitles } from 'src/_mock/assets';
// ----------------------------------------------------------------------

const MOCK_ID = _id[1];

const MOCK_TITLE = _postTitles[2];

const ROOTS = {
  AUTH: '/auth',
  AUTH_DEMO: '/auth-demo',
  DASHBOARD: '/contractor',
  SUPER_ADMIN_DASHBOARD: '/admin',
  ADMIN_DASHBOARD: '/admin-dashboard',
  SHOP_ADMIN: '/shop-admin'

};

// ----------------------------------------------------------------------

export const paths = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  termsAndConditions: '/terms-and-conditions',
  privacyPolicy: '/privacy-policy',
  contactSupport: '/contact-support-3R',
  faqs: '/faqs',
  page403: '/403',
  page404: '/404',
  page500: '/500',
  components: '/components',
  docs: 'https://docs.minimals.cc',
  changelog: 'https://docs.minimals.cc/changelog',
  zoneUI: 'https://mui.com/store/items/zone-landing-page/',
  minimalUI: 'https://mui.com/store/items/minimal-dashboard/',
  freeUI: 'https://mui.com/store/items/minimal-dashboard-free/',
  addPassword: '/add-password',
  login: '/login',
  register: '/register',
  changePassword: '/change-password',
  acceptTerms: `/accept-terms`,
  verify: '/verify',
  newPassword: '/new-password',
  shopFloorDisplay: '/build-mode/shop-floor-display',
  figma:
    'https://www.figma.com/file/hjxMnGUJCjY7pX8lQbS7kn/%5BPreview%5D-Minimal-Web.v5.4.0?type=design&node-id=0-1&mode=design&t=2fxnS70DuiTLGzND-0',
  product: {
    root: `/product`,
    checkout: `/product/checkout`,
    details: (id) => `/product/${id}`,
    demo: {
      details: `/product/${MOCK_ID}`,
    },
  },
  post: {
    root: `/post`,
    details: (title) => `/post/${paramCase(title)}`,
    demo: {
      details: `/post/${paramCase(MOCK_TITLE)}`,
    },
  },
  marketing: {
    root: `/marketing`,
    page: (slug) => `/marketing/${slug}`
  },
  // AUTH
  auth: {
    amplify: {
      login: `${ROOTS.AUTH}/amplify/login`,
      register: `${ROOTS.AUTH}/amplify/register`,
      verify: `${ROOTS.AUTH}/amplify/verify`,
      newPassword: `${ROOTS.AUTH}/amplify/new-password`,
      forgotPassword: `${ROOTS.AUTH}/amplify/forgot-password`,
    },
    superAdmin: {
      login: `/admin/login`,
      register: `/admin/register`,
      verify: `/admin/verify`,
      newPassword: `/admin/new-password`,
      forgotPassword: `/admin/forgot-password`,
    },
    jwt: {
      login: `${ROOTS.AUTH}/jwt/login`,
      register: `${ROOTS.AUTH}/jwt/register`,
      verify: `${ROOTS.AUTH}/jwt/verify`,
      newPassword: `${ROOTS.AUTH}/jwt/new-password`,
      forgotPassword: `${ROOTS.AUTH}/jwt/forgot-password`,
    },
    firebase: {
      login: `${ROOTS.AUTH}/firebase/login`,
      verify: `${ROOTS.AUTH}/firebase/verify`,
      register: `${ROOTS.AUTH}/firebase/register`,
      forgotPassword: `${ROOTS.AUTH}/firebase/forgot-password`,
    },
    auth0: {
      login: `${ROOTS.AUTH}/auth0/login`,
    },
  },
  authDemo: {
    classic: {
      login: `${ROOTS.AUTH_DEMO}/classic/login`,
      register: `${ROOTS.AUTH_DEMO}/classic/register`,
      forgotPassword: `${ROOTS.AUTH_DEMO}/classic/forgot-password`,
      newPassword: `${ROOTS.AUTH_DEMO}/classic/new-password`,
      verify: `${ROOTS.AUTH_DEMO}/classic/verify`,
    },
    modern: {
      login: `${ROOTS.AUTH_DEMO}/modern/login`,
      register: `${ROOTS.AUTH_DEMO}/modern/register`,
      forgotPassword: `${ROOTS.AUTH_DEMO}/modern/forgot-password`,
      newPassword: `${ROOTS.AUTH_DEMO}/modern/new-password`,
      verify: `${ROOTS.AUTH_DEMO}/modern/verify`,
    },
  },
  shopAdminDashboard: {
    root: ROOTS.SHOP_ADMIN,
    buildList: `${ROOTS.SHOP_ADMIN}/build-list`,
    buildDetail: (id) => `${ROOTS.SHOP_ADMIN}/build-detail/${id}`,
    notifications: `${ROOTS.SHOP_ADMIN}/notifications`,
    messageBoard: `${ROOTS.SHOP_ADMIN}/message-board`,
    account: `${ROOTS.SHOP_ADMIN}/account`,
  },
  // DASHBOARD
  dashboard: {
    root: ROOTS.DASHBOARD,
    quoteDetail: (id) => `${ROOTS.DASHBOARD}/quote/${id}`,
    jobs: {
      root: `${ROOTS.DASHBOARD}/jobs`,
      create: `${ROOTS.DASHBOARD}/jobs/create`,
      details: (id) => `${ROOTS.DASHBOARD}/jobs/${id}`,
      inventory: (id) => `${ROOTS.DASHBOARD}/jobs/${id}/inventory`,
    },
    customers: {
      root: `${ROOTS.DASHBOARD}/client`,
      create: `${ROOTS.DASHBOARD}/client/create`,
      details: (id) => `${ROOTS.DASHBOARD}/client/${id}`,
      email: `${ROOTS.DASHBOARD}/client/email-bulk`
    },
    inventory: {
      root: `${ROOTS.DASHBOARD}/inventory`,
      create: `${ROOTS.DASHBOARD}/inventory/create`
    },
    categories: {
      root: `${ROOTS.DASHBOARD}/categories`,
      create: `${ROOTS.DASHBOARD}/categories/create`
    },
    technicians: {
      root: `${ROOTS.DASHBOARD}/technicians`,
      create: `${ROOTS.DASHBOARD}/technicians/create`,
      assignTech: `${ROOTS.DASHBOARD}/technicians/assign`,
      details: (id) => `${ROOTS.DASHBOARD}/technicians/${id}`,
    },
    estimatorTemplates: {
      root: `${ROOTS.DASHBOARD}/estimator-templates`,
      create: `${ROOTS.DASHBOARD}/estimator-templates/create`,
      geographicAdjuster: `${ROOTS.DASHBOARD}/estimator-templates/geographic-adjustor`
    },
    invoices: {
      root: `${ROOTS.DASHBOARD}/invoices`,
      create: `${ROOTS.DASHBOARD}/invoices/create`,
      details: (id) => `${ROOTS.DASHBOARD}/invoices/${id}`,
    },
    transactions: {
      root: `${ROOTS.DASHBOARD}/transactions`,
      details: (id) => `${ROOTS.DASHBOARD}/transactions/${id}`,
    },
    estimatorSetting: {
      root: `${ROOTS.DASHBOARD}/estimator-settings`,
      geographicAdjuster: `${ROOTS.DASHBOARD}/estimator-settings/geographic-adjustor`,
      primaryService: `${ROOTS.DASHBOARD}/estimator-settings/primary-service`,
      additionalService: `${ROOTS.DASHBOARD}/estimator-settings/additional-service`,
      termsAndConditions: `${ROOTS.DASHBOARD}/estimator-settings/terms`,
      metric: `${ROOTS.DASHBOARD}/estimator-settings/metric`,
      jobType: `${ROOTS.DASHBOARD}/estimator-settings/job-type`,
    },
    settings: {
      root: `${ROOTS.DASHBOARD}/settings`,
      payment: `${ROOTS.DASHBOARD}/settings/payment`,
      estimatorSetting: {
        root: `${ROOTS.DASHBOARD}/settings/estimator`,
        geographicAdjuster: `${ROOTS.DASHBOARD}/settings/estimator/geographic-adjustor`,
        primaryService: `${ROOTS.DASHBOARD}/settings/estimator/primary-service`,
        additionalService: `${ROOTS.DASHBOARD}/settings/estimator/additional-service`,
        termsAndConditions: `${ROOTS.DASHBOARD}/settings/estimator/terms`,
        metric: `${ROOTS.DASHBOARD}/settings/estimator/metric`,
        jobType: `${ROOTS.DASHBOARD}/settings/estimator/job-type`,
        invoiceNotes: `${ROOTS.DASHBOARD}/settings/estimator/invoice-notes`,
        estimateNotes: `${ROOTS.DASHBOARD}/settings/estimator/estimate-notes`,
        reorderItems: (type) => `${ROOTS.DASHBOARD}/settings/estimator/reorder-items/${type}`,
      },
    },
    trainingAcademy: {
      root: `${ROOTS.DASHBOARD}/training-academy`,
      details: (id) => `${ROOTS.DASHBOARD}/training-academy/course/${id}/detail`,
      detailLesson: (courseId, lessonId) => `${ROOTS.DASHBOARD}/training-academy/course/${courseId}/lesson/${lessonId}`,
    },
    supportCenter: {
      root: `${ROOTS.DASHBOARD}/support-center`,
      // myBuild: `${ROOTS.DASHBOARD}/support-center/my-build`,
      // buildList: `${ROOTS.DASHBOARD}/support-center/build-list`,
      // notifications: (id)=>`${ROOTS.DASHBOARD}/support-center/notifications/${id}`,
      // messageBoard: `${ROOTS.DASHBOARD}/support-center/message-board`,
      // buildDetail: (id) => `${ROOTS.DASHBOARD}/support-center/build-detail/${id}`,
      resources: `${ROOTS.DASHBOARD}/support-center/resources`
    },
    buildMode:{
      root: `${ROOTS.DASHBOARD}/build-mode`,
      myBuild: `${ROOTS.DASHBOARD}/build-mode/my-build`,
      buildList: `${ROOTS.DASHBOARD}/build-mode/build-list`,
      notifications: (id)=>`${ROOTS.DASHBOARD}/build-mode/notifications/${id}`,
      messageBoard: `${ROOTS.DASHBOARD}/build-mode/message-board`,
      buildDetail: (id) => `${ROOTS.DASHBOARD}/build-mode/build-detail/${id}`,
    },
    detailResource: (id) => `${ROOTS.DASHBOARD}/support-center/resource/${id}/detail`,
    support: `${ROOTS.DASHBOARD}/contact-support`,
    recentActivities: `${ROOTS.DASHBOARD}/recent-activity`,
    collectPayments: `${ROOTS.DASHBOARD}/collect-payments`,
    viewCalendar: `${ROOTS.DASHBOARD}/calendar`,
    faq: `${ROOTS.DASHBOARD}/faq`,
    aboutUs: `${ROOTS.DASHBOARD}/about-us`,
    privacyPolicy: `${ROOTS.DASHBOARD}/privacy-policy`,
    termsAndConditions: `${ROOTS.DASHBOARD}/terms-and-conditions`,
    account: `${ROOTS.DASHBOARD}/account`,


    // OTHER ROUTES
    mail: `${ROOTS.DASHBOARD}/mail`,
    // chat: `${ROOTS.DASHBOARD}/chat`,
    blank: `${ROOTS.DASHBOARD}/blank`,
    kanban: `${ROOTS.DASHBOARD}/kanban`,
    calendar: `${ROOTS.DASHBOARD}/calendar`,
    fileManager: `${ROOTS.DASHBOARD}/file-manager`,
    permission: `${ROOTS.DASHBOARD}/permission`,
    general: {
      app: `${ROOTS.DASHBOARD}/app`,
      ecommerce: `${ROOTS.DASHBOARD}/ecommerce`,
      analytics: `${ROOTS.DASHBOARD}/analytics`,
      banking: `${ROOTS.DASHBOARD}/banking`,
      booking: `${ROOTS.DASHBOARD}/booking`,
      file: `${ROOTS.DASHBOARD}/file`,
    },
    user: {
      root: `${ROOTS.DASHBOARD}/user`,
      new: `${ROOTS.DASHBOARD}/user/new`,
      list: `${ROOTS.DASHBOARD}/user/list`,
      cards: `${ROOTS.DASHBOARD}/user/cards`,
      profile: `${ROOTS.DASHBOARD}/user/profile`,
      account: `${ROOTS.DASHBOARD}/user/account`,
      edit: (id) => `${ROOTS.DASHBOARD}/user/${id}/edit`,
      demo: {
        edit: `${ROOTS.DASHBOARD}/user/${MOCK_ID}/edit`,
      },
    },
  },

  superAdminDashboard: {
    root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/dashboard`,
    aiChat: `${ROOTS.SUPER_ADMIN_DASHBOARD}/ai-chat`,
    adminManagement: `${ROOTS.SUPER_ADMIN_DASHBOARD}/admin-management`,
    mail: `${ROOTS.SUPER_ADMIN_DASHBOARD}/mail`,
    chat: `${ROOTS.SUPER_ADMIN_DASHBOARD}/chat`,
    blank: `${ROOTS.SUPER_ADMIN_DASHBOARD}/blank`,
    kanban: `${ROOTS.SUPER_ADMIN_DASHBOARD}/kanban`,
    calendar: `${ROOTS.SUPER_ADMIN_DASHBOARD}/calendar`,
    fileManager: `${ROOTS.SUPER_ADMIN_DASHBOARD}/file-manager`,
    permission: `${ROOTS.SUPER_ADMIN_DASHBOARD}/permission`,
    general: {
      app: `${ROOTS.SUPER_ADMIN_DASHBOARD}/app`,
      ecommerce: `${ROOTS.SUPER_ADMIN_DASHBOARD}/ecommerce`,
      analytics: `${ROOTS.SUPER_ADMIN_DASHBOARD}/analytics`,
      banking: `${ROOTS.SUPER_ADMIN_DASHBOARD}/banking`,
      booking: `${ROOTS.SUPER_ADMIN_DASHBOARD}/booking`,
      file: `${ROOTS.SUPER_ADMIN_DASHBOARD}/file`,
    },
    technicians: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/technicians`,
      details: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/technicians/${id}`,
    },
    invoices: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/invoices`,
      create: `${ROOTS.SUPER_ADMIN_DASHBOARD}/invoices/create`,
      details: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/invoices/${id}`,
    },
    customers: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/client`,
      details: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/client/${id}`,
    },
    estimatorTemplates: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/estimator-templates`,
      create: `${ROOTS.SUPER_ADMIN_DASHBOARD}/estimator-templates/create`,
      details: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/estimator-templates/${id}`
    },
    user: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/user`,
      new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/user/new`,
      list: `${ROOTS.SUPER_ADMIN_DASHBOARD}/user/list`,
      cards: `${ROOTS.SUPER_ADMIN_DASHBOARD}/user/cards`,
      profile: `${ROOTS.SUPER_ADMIN_DASHBOARD}/user/profile`,
      account: `${ROOTS.SUPER_ADMIN_DASHBOARD}/user/account`,
      edit: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/user/${id}/edit`,
      demo: {
        edit: `${ROOTS.SUPER_ADMIN_DASHBOARD}/user/${MOCK_ID}/edit`,
      },
    },
    quoteDetail: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/quote/${id}`,
    jobs: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/jobs`,
      details: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/jobs/${id}`,
    },
    contractor: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/contractor`,
      details: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/contractor/${id}`,
    },
    metric: `${ROOTS.SUPER_ADMIN_DASHBOARD}/metric`,

    estimatorSetting: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/estimator-setting`,
      geographicAdjuster: `${ROOTS.SUPER_ADMIN_DASHBOARD}/estimator-setting/geographic-adjustor`,
      primaryService: `${ROOTS.SUPER_ADMIN_DASHBOARD}/estimator-setting/primary-service`,
      additionalService: `${ROOTS.SUPER_ADMIN_DASHBOARD}/estimator-setting/additional-service`,
      termsAndConditions: `${ROOTS.SUPER_ADMIN_DASHBOARD}/estimator-setting/terms`,
      metric: `${ROOTS.SUPER_ADMIN_DASHBOARD}/estimator-setting/metric`,
      invoiceNotes: `${ROOTS.SUPER_ADMIN_DASHBOARD}/estimator-setting/invoice-notes`,
      estimateNotes: `${ROOTS.SUPER_ADMIN_DASHBOARD}/estimator-setting/estimate-notes`,
      categories: (type) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/estimator-setting/categories?type=${type}`,
    },
    jobType: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/job-type`,
      list: `${ROOTS.SUPER_ADMIN_DASHBOARD}/job-type/list`
    },
    termsAndConditions: `${ROOTS.SUPER_ADMIN_DASHBOARD}/terms`,
    // settings: `${ROOTS.SUPER_ADMIN_DASHBOARD}/settings`,
    settings: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/settings`,
      general: `${ROOTS.SUPER_ADMIN_DASHBOARD}/settings/general`,
      customizePages: `${ROOTS.SUPER_ADMIN_DASHBOARD}/settings/customize-pages`,
      addCustomizePage: `${ROOTS.SUPER_ADMIN_DASHBOARD}/settings/customize-pages/add`,
      editCustomizePage: (slug) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/settings/customize-pages/${slug}/edit`
    },
    requests: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/requests`,
      contactSupport: `${ROOTS.SUPER_ADMIN_DASHBOARD}/requests/contact-support`,
      contactUs: `${ROOTS.SUPER_ADMIN_DASHBOARD}/requests/contact-us`
    },
    trainingAcademy: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/training-academy`,
      alerts: `${ROOTS.SUPER_ADMIN_DASHBOARD}/training-academy/alerts`,
      categories: `${ROOTS.SUPER_ADMIN_DASHBOARD}/training-academy/categories`,
      courses: `${ROOTS.SUPER_ADMIN_DASHBOARD}/training-academy/courses`,
      addCourse: `${ROOTS.SUPER_ADMIN_DASHBOARD}/training-academy/course/add`,
      reorderCourses: `${ROOTS.SUPER_ADMIN_DASHBOARD}/training-academy/courses/reorder`,
      reorderLessons: (courseId) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/training-academy/course/${courseId}/lessons/reorder`,
      editCourse: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/training-academy/course/edit/${id}`,
      detailCourse: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/training-academy/course/detail/${id}`,
      addLesson: (courseId) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/training-academy/course/${courseId}/lesson/add`,
      editLesson: (courseId, id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/training-academy/course/${courseId}/lesson/edit/${id}`,
      courseLessonList: (courseId) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/training-academy/course/${courseId}/lessons`,
    },
    account: `${ROOTS.SUPER_ADMIN_DASHBOARD}/account`,
    userManual: `${ROOTS.SUPER_ADMIN_DASHBOARD}/docs`,
    faqs: `${ROOTS.SUPER_ADMIN_DASHBOARD}/faqs`,
    privacyPolicy: `${ROOTS.SUPER_ADMIN_DASHBOARD}/privacy`,
    aboutUs: `${ROOTS.SUPER_ADMIN_DASHBOARD}/about-us`,

    supportCenter: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center`,
      categories: `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center/categories`,
      resourcePackages: `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center/resource-packages`,
      assignResourcePackage: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center/resource-packages/${id}/assign-resources`,
      resources: `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center/resources`,
      addResource: `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center/resource/add`,
      editResource: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center/resource/${id}/edit`,
      detailResource: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center/resource/${id}/detail`,
      user: `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center/user`,
      assignUserResources: (userId, packageId) => packageId
        ? `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center/user/${userId}/assign-resources/${packageId}`
        : `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center/user/${userId}/assign-resources`,
      editUserResources: (userId) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center/user/${userId}/edit-resources`,
      reorderResources: `${ROOTS.SUPER_ADMIN_DASHBOARD}/support-center/resource/reorder`,
    },
    buildMode: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/build-mode`,
      buildList: `${ROOTS.SUPER_ADMIN_DASHBOARD}/build-mode/build-list`,
      activeBuilds: `${ROOTS.SUPER_ADMIN_DASHBOARD}/build-mode/active-builds`,
      buildDetail: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/build-mode/build-detail/${id}`,
      buildStages: `${ROOTS.SUPER_ADMIN_DASHBOARD}/build-mode/build-stages`,
      shopAdmins: `${ROOTS.SUPER_ADMIN_DASHBOARD}/build-mode/shop-admins`,
      shopAdminDetail: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/build-mode/shop-admin-detail/${id}`,
      shopFloorDisplay: `${ROOTS.SUPER_ADMIN_DASHBOARD}/build-mode/shop-floor-display`,
      messageBoard: `${ROOTS.SUPER_ADMIN_DASHBOARD}/build-mode/message-board`,
      notifications: `${ROOTS.SUPER_ADMIN_DASHBOARD}/build-mode/notifications`,
      buildNotification: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/build-mode/notifications/${id}`,
    },


    business: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/business`,
      new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/business/new`,
      list: `${ROOTS.SUPER_ADMIN_DASHBOARD}/business/list`,
      edit: (id) => `${ROOTS.DASHBOARD}/business/${id}/edit`,
    },
    branch: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/branch`,
      new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/branch/new`,
      list: `${ROOTS.SUPER_ADMIN_DASHBOARD}/branch/list`,
      edit: (id) => `${ROOTS.DASHBOARD}/branch/${id}/edit`,
    },
    category: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/category`,
      new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/category/new`,
      edit: (id) => `${ROOTS.DASHBOARD}/category/${id}/edit`,
    },
    project: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/project`,
      new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/project/new`,
      edit: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/project/${id}/edit`,
    },
    payrol: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/payrol`,
      new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/payrol/new`,
      edit: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/payrol/${id}/edit`,
    },
    service: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/service`,
      new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/service/new`,
      details: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/service/${paramCase(id)}`,
      edit: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/service/${paramCase(id)}/edit`,
    },
    attendance: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/attendance`,
      new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/attendance/new`,
      list: `${ROOTS.SUPER_ADMIN_DASHBOARD}/attendance/list`,
      edit: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/attendance/${id}/edit`,
    },
    product: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/product`,
      new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/product/new`,
      details: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/product/${id}`,
      edit: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/product/${id}/edit`,
      demo: {
        details: `${ROOTS.SUPER_ADMIN_DASHBOARD}/product/${MOCK_ID}`,
        edit: `${ROOTS.SUPER_ADMIN_DASHBOARD}/product/${MOCK_ID}/edit`,
      },
    },
    invoice: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/invoice`,
      new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/invoice/new`,
      details: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/invoice/${id}`,
      edit: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/invoice/${id}/edit`,
      demo: {
        details: `${ROOTS.SUPER_ADMIN_DASHBOARD}/invoice/${MOCK_ID}`,
        edit: `${ROOTS.SUPER_ADMIN_DASHBOARD}/invoice/${MOCK_ID}/edit`,
      },
    },
    post: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/post`,
      new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/post/new`,
      details: (title) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/post/${paramCase(title)}`,
      edit: (title) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/post/${paramCase(title)}/edit`,
      demo: {
        details: `${ROOTS.SUPER_ADMIN_DASHBOARD}/post/${paramCase(MOCK_TITLE)}`,
        edit: `${ROOTS.SUPER_ADMIN_DASHBOARD}/post/${paramCase(MOCK_TITLE)}/edit`,
      },
    },
    order: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/order`,
      details: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/order/${id}`,
      demo: {
        details: `${ROOTS.SUPER_ADMIN_DASHBOARD}/order/${MOCK_ID}`,
      },
    },
    // job: {
    //   root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/job`,
    //   new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/job/new`,
    //   details: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/job/${id}`,
    //   edit: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/job/${id}/edit`,
    //   demo: {
    //     details: `${ROOTS.SUPER_ADMIN_DASHBOARD}/job/${MOCK_ID}`,
    //     edit: `${ROOTS.SUPER_ADMIN_DASHBOARD}/job/${MOCK_ID}/edit`,
    //   },
    // },
    tour: {
      root: `${ROOTS.SUPER_ADMIN_DASHBOARD}/tour`,
      new: `${ROOTS.SUPER_ADMIN_DASHBOARD}/tour/new`,
      details: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/tour/${id}`,
      edit: (id) => `${ROOTS.SUPER_ADMIN_DASHBOARD}/tour/${id}/edit`,
      demo: {
        details: `${ROOTS.SUPER_ADMIN_DASHBOARD}/tour/${MOCK_ID}`,
        edit: `${ROOTS.SUPER_ADMIN_DASHBOARD}/tour/${MOCK_ID}/edit`,
      },
    },
  },

  adminDashboard: {
    root: ROOTS.ADMIN_DASHBOARD,
    mail: `${ROOTS.ADMIN_DASHBOARD}/mail`,
    chat: `${ROOTS.ADMIN_DASHBOARD}/chat`,
    blank: `${ROOTS.ADMIN_DASHBOARD}/blank`,
    kanban: `${ROOTS.ADMIN_DASHBOARD}/kanban`,
    calendar: `${ROOTS.ADMIN_DASHBOARD}/calendar`,
    fileManager: `${ROOTS.ADMIN_DASHBOARD}/file-manager`,
    permission: `${ROOTS.ADMIN_DASHBOARD}/permission`,
    general: {
      app: `${ROOTS.ADMIN_DASHBOARD}/app`,
      ecommerce: `${ROOTS.ADMIN_DASHBOARD}/ecommerce`,
      analytics: `${ROOTS.ADMIN_DASHBOARD}/analytics`,
      banking: `${ROOTS.ADMIN_DASHBOARD}/banking`,
      booking: `${ROOTS.ADMIN_DASHBOARD}/booking`,
      file: `${ROOTS.ADMIN_DASHBOARD}/file`,
    },
    user: {
      root: `${ROOTS.ADMIN_DASHBOARD}/user`,
      new: `${ROOTS.ADMIN_DASHBOARD}/user/new`,
      list: `${ROOTS.ADMIN_DASHBOARD}/user/list`,
      cards: `${ROOTS.ADMIN_DASHBOARD}/user/cards`,
      profile: `${ROOTS.ADMIN_DASHBOARD}/user/profile`,
      account: `${ROOTS.ADMIN_DASHBOARD}/user/account`,
      edit: (id) => `${ROOTS.ADMIN_DASHBOARD}/user/${id}/edit`,
      demo: {
        edit: `${ROOTS.ADMIN_DASHBOARD}/user/${MOCK_ID}/edit`,
      },
    },
    business: {
      root: `${ROOTS.ADMIN_DASHBOARD}/business`,
      new: `${ROOTS.ADMIN_DASHBOARD}/business/new`,
      list: `${ROOTS.ADMIN_DASHBOARD}/business/list`,
      edit: (id) => `${ROOTS.ADMIN_DASHBOARD}/business/${id}/edit`,
    },
    branch: {
      root: `${ROOTS.ADMIN_DASHBOARD}/branch`,
      new: `${ROOTS.ADMIN_DASHBOARD}/branch/new`,
      list: `${ROOTS.ADMIN_DASHBOARD}/branch/list`,
      edit: (id) => `${ROOTS.ADMIN_DASHBOARD}/branch/${id}/edit`,
    },
    category: {
      root: `${ROOTS.ADMIN_DASHBOARD}/category`,
      new: `${ROOTS.ADMIN_DASHBOARD}/category/new`,
      edit: (id) => `${ROOTS.ADMIN_DASHBOARD}/category/${id}/edit`,
    },
    project: {
      root: `${ROOTS.ADMIN_DASHBOARD}/project`,
      new: `${ROOTS.ADMIN_DASHBOARD}/project/new`,
      edit: (id) => `${ROOTS.ADMIN_DASHBOARD}/project/${id}/edit`,
    },
    payrol: {
      root: `${ROOTS.ADMIN_DASHBOARD}/payrol`,
      new: `${ROOTS.ADMIN_DASHBOARD}/payrol/new`,
      edit: (id) => `${ROOTS.ADMIN_DASHBOARD}/payrol/${id}/edit`,
    },
    service: {
      root: `${ROOTS.ADMIN_DASHBOARD}/service`,
      new: `${ROOTS.ADMIN_DASHBOARD}/service/new`,
      details: (id) => `${ROOTS.ADMIN_DASHBOARD}/service/${paramCase(id)}`,
      edit: (id) => `${ROOTS.ADMIN_DASHBOARD}/service/${paramCase(id)}/edit`,
    },
    attendance: {
      root: `${ROOTS.ADMIN_DASHBOARD}/attendance`,
      new: `${ROOTS.ADMIN_DASHBOARD}/attendance/new`,
      list: `${ROOTS.ADMIN_DASHBOARD}/attendance/list`,
      edit: (id) => `${ROOTS.ADMIN_DASHBOARD}/attendance/${id}/edit`,
    },
    product: {
      root: `${ROOTS.ADMIN_DASHBOARD}/product`,
      new: `${ROOTS.ADMIN_DASHBOARD}/product/new`,
      details: (id) => `${ROOTS.ADMIN_DASHBOARD}/product/${id}`,
      edit: (id) => `${ROOTS.ADMIN_DASHBOARD}/product/${id}/edit`,
      demo: {
        details: `${ROOTS.ADMIN_DASHBOARD}/product/${MOCK_ID}`,
        edit: `${ROOTS.ADMIN_DASHBOARD}/product/${MOCK_ID}/edit`,
      },
    },
    invoice: {
      root: `${ROOTS.ADMIN_DASHBOARD}/invoice`,
      new: `${ROOTS.ADMIN_DASHBOARD}/invoice/new`,
      details: (id) => `${ROOTS.ADMIN_DASHBOARD}/invoice/${id}`,
      edit: (id) => `${ROOTS.ADMIN_DASHBOARD}/invoice/${id}/edit`,
      demo: {
        details: `${ROOTS.ADMIN_DASHBOARD}/invoice/${MOCK_ID}`,
        edit: `${ROOTS.ADMIN_DASHBOARD}/invoice/${MOCK_ID}/edit`,
      },
    },
    post: {
      root: `${ROOTS.ADMIN_DASHBOARD}/post`,
      new: `${ROOTS.ADMIN_DASHBOARD}/post/new`,
      details: (title) => `${ROOTS.ADMIN_DASHBOARD}/post/${paramCase(title)}`,
      edit: (title) => `${ROOTS.ADMIN_DASHBOARD}/post/${paramCase(title)}/edit`,
      demo: {
        details: `${ROOTS.ADMIN_DASHBOARD}/post/${paramCase(MOCK_TITLE)}`,
        edit: `${ROOTS.ADMIN_DASHBOARD}/post/${paramCase(MOCK_TITLE)}/edit`,
      },
    },
    order: {
      root: `${ROOTS.ADMIN_DASHBOARD}/order`,
      details: (id) => `${ROOTS.ADMIN_DASHBOARD}/order/${id}`,
      demo: {
        details: `${ROOTS.ADMIN_DASHBOARD}/order/${MOCK_ID}`,
      },
    },
    job: {
      root: `${ROOTS.ADMIN_DASHBOARD}/job`,
      new: `${ROOTS.ADMIN_DASHBOARD}/job/new`,
      details: (id) => `${ROOTS.ADMIN_DASHBOARD}/job/${id}`,
      edit: (id) => `${ROOTS.ADMIN_DASHBOARD}/job/${id}/edit`,
      demo: {
        details: `${ROOTS.ADMIN_DASHBOARD}/job/${MOCK_ID}`,
        edit: `${ROOTS.ADMIN_DASHBOARD}/job/${MOCK_ID}/edit`,
      },
    },
    tour: {
      root: `${ROOTS.ADMIN_DASHBOARD}/tour`,
      new: `${ROOTS.ADMIN_DASHBOARD}/tour/new`,
      details: (id) => `${ROOTS.ADMIN_DASHBOARD}/tour/${id}`,
      edit: (id) => `${ROOTS.ADMIN_DASHBOARD}/tour/${id}/edit`,
      demo: {
        details: `${ROOTS.ADMIN_DASHBOARD}/tour/${MOCK_ID}`,
        edit: `${ROOTS.ADMIN_DASHBOARD}/tour/${MOCK_ID}/edit`,
      },
    },
  },

};
